import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, 
  Plus, 
  Trash2, 
  UserCircle,
  Shield,
  Key,
  Mail,
  Pencil,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module labels for display
const MODULE_LABELS = {
  dashboard: "Dashboard",
  calendario: "Calendario",
  agenda: "Agenda Clínica",
  no_asistieron: "No Asistieron",
  pacientes: "Pacientes",
  crm_cirugias: "CRM Cirugías",
  ventas: "Punto de Venta",
  finanzas: "Finanzas",
  inventario: "Inventario",
  proveedores: "Proveedores",
  compras: "Compras",
  whatsapp: "WhatsApp",
  configuracion: "Configuración"
};

const ALL_MODULES = Object.keys(MODULE_LABELS);

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [selectedPermisos, setSelectedPermisos] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNewUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'admin' });
      fetchUsers();
      toast.success('Usuario creado exitosamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${userName}"?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    try {
      await axios.put(`${API}/auth/users/${selectedUser.id}/password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Contraseña de ${selectedUser.name} actualizada`);
      setShowChangePassword(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const openChangePassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowChangePassword(true);
  };

  const openEditUser = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'admin'
    });
    setShowEditUser(true);
  };

  const openPermissions = (user) => {
    setPermissionsUser(user);
    setSelectedPermisos(user.permisos || ALL_MODULES);
    setShowPermissions(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Usuario actualizado exitosamente');
      setShowEditUser(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API}/users/${permissionsUser.id}`, {
        permisos: selectedPermisos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Permisos actualizados');
      setShowPermissions(false);
      setPermissionsUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar permisos');
    }
  };

  const togglePermiso = (modulo) => {
    setSelectedPermisos(prev => 
      prev.includes(modulo) 
        ? prev.filter(p => p !== modulo)
        : [...prev, modulo]
    );
  };

  const selectAllPermisos = () => setSelectedPermisos(ALL_MODULES);
  const clearAllPermisos = () => setSelectedPermisos([]);

  const getRoleBadge = (role) => {
    const styles = {
      superadmin: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      secretary: 'bg-green-100 text-green-700',
      caja: 'bg-amber-100 text-amber-700'
    };
    const labels = {
      superadmin: 'Super Admin',
      admin: 'Administrador',
      secretary: 'Secretaria',
      caja: 'Caja'
    };
    return <Badge className={styles[role] || 'bg-gray-100 text-gray-700'}>{labels[role] || role}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Usuarios</h1>
          <p className="text-slate-500 mt-1">Gestiona los usuarios del sistema</p>
        </div>
        <Button 
          onClick={() => setShowNewUser(true)}
          className="bg-[#0891B2] hover:bg-[#0e7490]"
          data-testid="new-user-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Users List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Lista de Usuarios ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay usuarios registrados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#0B3B71]/10 flex items-center justify-center">
                      <UserCircle className="h-6 w-6 text-[#0B3B71]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {user.email || user.username}
                      </p>
                      {user.permisos && user.permisos.length < ALL_MODULES.length && (
                        <p className="text-xs text-amber-600 mt-1">
                          {user.permisos.length} módulos activos
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(user.role)}
                    {/* Permissions button - only for superadmin */}
                    {currentUser?.role === 'superadmin' && user.role !== 'superadmin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        onClick={() => openPermissions(user)}
                        data-testid={`permissions-user-${user.id}`}
                        title="Configurar permisos"
                      >
                        <Shield className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Show edit button for superadmin (can edit anyone) or admin (can edit non-superadmin) */}
                    {(currentUser?.role === 'superadmin' || 
                      (currentUser?.role === 'admin' && user.role !== 'superadmin')) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => openEditUser(user)}
                        data-testid={`edit-user-${user.id}`}
                        title="Editar usuario"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Show change password button for superadmin */}
                    {currentUser?.role === 'superadmin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => openChangePassword(user)}
                        data-testid={`change-password-${user.id}`}
                        title="Cambiar contraseña"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Show delete button for superadmin (can delete anyone) or admin (can delete non-superadmin) */}
                    {(currentUser?.role === 'superadmin' || currentUser?.role === 'admin') && 
                     user.id !== currentUser?.id && 
                     !(currentUser?.role === 'admin' && user.role === 'superadmin') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        data-testid={`delete-user-${user.id}`}
                        title="Eliminar usuario"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New User Dialog */}
      <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Ej: Juan Pérez"
                required
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Correo Electrónico</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value.toLowerCase().trim() })}
                placeholder="correo@ejemplo.com"
                required
                data-testid="new-user-email"
              />
              <p className="text-xs text-slate-400">Se usará para iniciar sesión</p>
            </div>
            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select 
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue placeholder="Selecciona un rol" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="secretary">Secretaria</SelectItem>
                  <SelectItem value="caja">Caja</SelectItem>
                  {currentUser?.role === 'superadmin' && (
                    <SelectItem value="superadmin">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowNewUser(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" data-testid="create-user-button">
                Crear Usuario
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog - Only for superadmin */}
      <Dialog open={showPermissions} onOpenChange={setShowPermissions}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Configurar Permisos
            </DialogTitle>
          </DialogHeader>
          {permissionsUser && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Usuario:</p>
                <p className="font-medium">{permissionsUser.name}</p>
                <p className="text-sm text-slate-400">{permissionsUser.email}</p>
              </div>
              
              <div className="flex justify-between items-center">
                <Label className="text-sm font-medium">Módulos con acceso</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllPermisos}>
                    Todos
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={clearAllPermisos}>
                    Ninguno
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-1">
                {ALL_MODULES.map((modulo) => (
                  <div 
                    key={modulo}
                    className={`flex items-center space-x-2 p-2 rounded-md cursor-pointer transition-colors ${
                      selectedPermisos.includes(modulo) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => togglePermiso(modulo)}
                  >
                    <Checkbox 
                      checked={selectedPermisos.includes(modulo)}
                      onCheckedChange={() => togglePermiso(modulo)}
                      id={`permiso-${modulo}`}
                    />
                    <label 
                      htmlFor={`permiso-${modulo}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {MODULE_LABELS[modulo]}
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="text-xs text-slate-500 text-center">
                {selectedPermisos.length} de {ALL_MODULES.length} módulos seleccionados
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setShowPermissions(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSavePermissions}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="save-permissions-button"
                >
                  Guardar Permisos
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <Key className="h-5 w-5" />
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Usuario:</p>
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-sm text-slate-400">{selectedUser.email || selectedUser.username}</p>
              </div>
              <div className="space-y-2">
                <Label>Nueva Contraseña</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <p className="text-xs text-slate-400">
                  Debe incluir mayúsculas, minúsculas y números
                </p>
              </div>
              <div className="space-y-2">
                <Label>Confirmar Contraseña</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setShowChangePassword(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  data-testid="save-password-button"
                >
                  Guardar Contraseña
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Usuario
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  placeholder="Ej: Juan Pérez"
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Correo Electrónico</Label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value.toLowerCase().trim() })}
                  placeholder="correo@ejemplo.com"
                  required
                  data-testid="edit-user-email"
                />
                <p className="text-xs text-slate-400">Se usará para iniciar sesión</p>
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select 
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue placeholder="Selecciona un rol" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="secretary">Secretaria</SelectItem>
                    <SelectItem value="caja">Caja</SelectItem>
                    {currentUser?.role === 'superadmin' && (
                      <SelectItem value="superadmin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setShowEditUser(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]"
                  data-testid="save-user-button"
                >
                  Guardar Cambios
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
